<div class="generate-container">
  <!-- Tab Launcher for Compliance Request -->
  <app-compliance-tab-launcher (openPopup)="openRequestCompliancePopup()"></app-compliance-tab-launcher>

  <!-- Header Section -->
  <div class="generate-header mb-4">
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h3 class="mb-2">Generate Reports</h3>
        <p class="text-muted mb-0">Select a report and delivery options for on-demand or scheduled reporting needs</p>
      </div>
      <div class="d-flex gap-2">
        <button
          type="submit"
          kendoButton
          class="cyflare-btn-primary cyflare-btn-lg"
          [disabled]="!canGenerate"
          (click)="generateReports()"
        >
          @if (isGenerating()) {
            <span class="spinner-border spinner-border-sm me-2"></span>
            @if (generateForm.get('deliveryMethod')?.value === 'schedule') {
              Saving Schedule...
            } @else {
              Generating...
            }
          } @else {
            @if (generateForm.get('deliveryMethod')?.value === 'schedule') {
              Save Schedule
            } @else if (generateForm.get('deliveryMethod')?.value === 'email') {
              Generate & Email Reports
            } @else if (generateForm.get('deliveryMethod')?.value === 'storage') {
              Generate & Save to Storage
            } @else {
              Generate Reports
            }
          }
        </button>
      </div>
    </div>
  </div>

  @if (isLoading()) {
    <div class="text-center py-5">
      <div
        class="spinner-border text-primary"
        role="status"
      >
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-3">Loading configuration...</p>
    </div>
  } @else {
    <form
      [formGroup]="generateForm"
      (ngSubmit)="generateReports()"
    >
      <div class="row g-4">
        <!-- Left Column: Report Type Selection -->
        <div class="col-lg-8">
          <!-- Filter & Report Type Selection -->
          <div class="card mb-3">
            <div class="card-body">
              <h5 class="card-title mb-3">Select Report Type</h5>

              <!-- Search -->
              <div class="mb-3">
                <input
                  type="text"
                  class="form-control"
                  placeholder="Search reports..."
                  [value]="searchQuery()"
                  (input)="searchQuery.set($any($event.target).value)"
                />
              </div>

              <!-- Collapsible Filter Section -->
              <div class="filter-section mb-3">
                <button
                  type="button"
                  class="btn btn-link w-100 text-start p-0 d-flex justify-content-between align-items-center filter-toggle"
                  (click)="toggleFilters()"
                >
                  <span class="text-muted small fw-semibold">
                    <kendo-svg-icon
                      [icon]="filterIcon"
                      class="me-2"
                    ></kendo-svg-icon>
                    Advanced Filters
                    @if (hasActiveFilters()) {
                      <span class="badge bg-primary ms-2">{{ activeFilterCount() }}</span>
                    }
                  </span>
                  <kendo-svg-icon [icon]="showFilters() ? chevronUpIcon : chevronDownIcon"></kendo-svg-icon>
                </button>

                @if (showFilters()) {
                  <div class="filter-content mt-3">
                    <!-- Category Pills -->
                    <div class="mb-3">
                      <label class="form-label small text-muted fw-semibold">Filter by Category</label>
                      <div class="category-pills">
                        @for (cat of categories; track cat.id) {
                          <button
                            type="button"
                            class="btn btn-sm"
                            [class.btn-primary]="selectedCategoryFilter() === cat.id"
                            [class.btn-outline-primary]="selectedCategoryFilter() !== cat.id"
                            (click)="selectCategory(cat.id)"
                          >
                            {{ cat.category }}
                          </button>
                        }
                      </div>
                    </div>

                    <!-- Compliance Framework Pills -->
                    <div class="mb-3">
                      <label class="form-label small text-muted fw-semibold">Filter by Compliance Framework</label>
                      <div class="compliance-framework-pills">
                        @for (framework of complianceFrameworks; track framework) {
                          <button
                            type="button"
                            class="btn btn-sm"
                            [class.btn-success]="selectedFrameworkFilter() === framework.id"
                            [class.btn-outline-success]="selectedFrameworkFilter() !== framework.id"
                            (click)="selectFramework(framework.id)"
                          >
                            {{ framework.compliance_framework }}
                          </button>
                        }
                      </div>
                    </div>

                    <!-- Specific Compliance Control Filter -->
                    <div class="mb-3">
                      <label class="form-label small text-muted fw-semibold">
                        Filter by Specific Controls (Optional)
                      </label>
                      <kendo-multiselect
                        [data]="complianceTags()"
                        textField="name"
                        valueField="id"
                        [valuePrimitive]="true"
                        [value]="selectedComplianceFilters()"
                        (valueChange)="selectedComplianceFilters.set($event)"
                        placeholder="Select compliance tags..."
                        class="w-100"
                      >
                        <ng-template
                          kendoMultiSelectItemTemplate
                          let-dataItem
                        >
                          <span
                            class="badge me-2"
                            [style.background-color]="dataItem.color"
                          >
                            {{ dataItem.category }}
                          </span>
                          {{ dataItem.name }}
                        </ng-template>
                      </kendo-multiselect>
                    </div>

                    @if (hasActiveFilters()) {
                      <div class="mb-0">
                        <button
                          type="button"
                          kendoButton
                          class="cyflare-btn-secondary cyflare-btn-sm"
                          (click)="clearAllFilters()"
                        >
                          Clear All Filters
                        </button>
                      </div>
                    }
                  </div>
                }
              </div>

              <!-- Report Type Cards -->
              <div class="report-type-grid">
                @for (report of filteredReportTypes(); track report.id) {
                  <div
                    class="report-type-card"
                    [class.selected]="generateForm.get('reportType')?.value === report.id"
                    (click)="selectReportType(report.id)"
                  >
                    <div class="report-card-header">
                      <h6 class="mb-1">{{ report.name }}</h6>
                      <span class="badge bg-secondary">{{ report.format }}</span>
                    </div>
                    <p class="report-description text-muted small mb-2">{{ report.description }}</p>
                    <div class="report-card-footer">
                      @for (cat of report.reportTemplate.categories; track cat.id) {
                        <span class="badge bg-light text-dark me-1">{{ cat.category }}</span>
                      }

                      @if (report.reportTemplate.complianceTags.length > 0) {
                        <div class="compliance-tags mt-2">
                          @for (badge of getUniqueComplianceBadges(report).slice(0, 3); track badge.category) {
                            <span
                              class="badge me-1 mb-1"
                              [style.background-color]="badge.color"
                              [title]="badge.category + ': ' + badge.controlNames.join(', ')"
                            >
                              {{ badge.category }}
                            </span>
                          }
                          @if (getUniqueComplianceBadges(report).length > 3) {
                            <span class="badge bg-secondary mb-1">
                              +{{ getUniqueComplianceBadges(report).length - 3 }}
                            </span>
                          }
                        </div>
                      }
                    </div>
                  </div>
                } @empty {
                  <div class="text-center py-4 text-muted">
                    <p class="mb-0">No reports match your filters</p>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column: All Other Settings -->
        <div class="col-lg-4">
          <!-- Organization Selection (hidden for org type 1 or when no child customers) -->
          @if (userPrimaryOrgType() !== 1 && customers().length > 0) {
            <div class="card mb-3">
              <div class="card-body">
                <h5 class="card-title mb-3">Select Organizations</h5>

                <!-- Section 1: My Organization -->
                <div class="border rounded p-3 mb-3">
                  <div class="text-uppercase text-muted small fw-bold mb-2">My Organization</div>
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      formControlName="includeSelfOrganization"
                      id="includeSelfOrg"
                    />
                    <label
                      class="form-check-label d-flex align-items-center gap-2"
                      for="includeSelfOrg"
                    >
                      <span class="fw-medium">{{ selectedOrgName() }}</span>
                      <span class="badge bg-primary">{{ selectedOrgTypeLabel() }}</span>
                    </label>
                  </div>
                  <div class="text-muted small mt-1 ms-4">Generate reports for your own organization</div>
                </div>

                <!-- Section 2: Child Organizations -->
                <div class="border rounded p-3">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="text-uppercase text-muted small fw-bold">Child Organizations</div>
                    <div class="btn-group btn-group-sm">
                      <button
                        type="button"
                        kendoButton
                        class="cyflare-btn-secondary cyflare-btn-sm"
                        (click)="selectAllCustomers()"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        kendoButton
                        class="cyflare-btn-secondary cyflare-btn-sm"
                        (click)="clearCustomerSelection()"
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <kendo-multiselect
                    formControlName="selectedCustomers"
                    [kendoMultiSelectSummaryTag]="selectedCustomerCount > 4 ? 0 : selectedCustomerCount"
                    [data]="customers()"
                    textField="name"
                    valueField="id"
                    [valuePrimitive]="true"
                    placeholder="Select child customers..."
                    class="w-100"
                  >
                    <ng-template
                      kendoMultiSelectItemTemplate
                      let-dataItem
                    >
                      <div class="d-flex justify-content-between align-items-center w-100">
                        <span>{{ dataItem.name }}</span>
                        <span class="badge bg-secondary ms-2">{{ dataItem.industry }}</span>
                      </div>
                    </ng-template>

                    <ng-template
                      kendoMultiSelectGroupTagTemplate
                      let-dataItems
                    >
                      {{ dataItems.length }} customers selected
                    </ng-template>
                  </kendo-multiselect>

                  @if (selectedCustomerCount > 0) {
                    <div class="text-muted small mt-2">{{ selectedCustomerCount }} selected</div>
                  }
                </div>

                <!-- Total Summary -->
                @if (getTotalSelectedCount() > 0) {
                  <div class="mt-3 p-2 bg-light rounded text-center">
                    <span class="fw-medium">{{ getTotalSelectedCount() }}</span>
                    organization(s) will be included in report
                  </div>
                } @else {
                  <div class="mt-3 p-2 bg-warning-subtle rounded text-center text-warning-emphasis small">
                    Please select at least one organization
                  </div>
                }
              </div>
            </div>
          }

          <!-- Aggregate Report Option -->
          @if (showAggregateCheckbox()) {
            <div class="card mb-3">
              <div class="card-body">
                <h5 class="card-title mb-3">Report Output</h5>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    [checked]="aggregateReport()"
                    (change)="aggregateReport.set($any($event.target).checked)"
                    id="aggregateReport"
                  />
                  <label class="form-check-label fw-medium" for="aggregateReport">
                    Aggregate into single file
                  </label>
                </div>
                <div class="text-muted small mt-1 ms-4">
                  Combine all selected organizations into a single CSV file instead of generating separate files per organization
                </div>
              </div>
            </div>
          }

          <!-- Delivery Configuration -->
          <div class="card mb-3">
            <div class="card-body">
              <h5 class="card-title mb-3">Delivery Configuration</h5>

              <div class="form-check mb-3">
                <input
                  class="form-check-input"
                  type="radio"
                  formControlName="deliveryMethod"
                  value="download"
                  id="deliveryDownload"
                />
                <label
                  class="form-check-label"
                  for="deliveryDownload"
                >
                  Generate Now For Download
                </label>
              </div>

              @if (generateForm.get('deliveryMethod')?.value === 'email') {
                <div class="ms-4 mb-3">
                  <label class="form-label">Email Recipients</label>
                  <input
                    type="text"
                    class="form-control"
                    formControlName="emailRecipients"
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <div class="form-text">Comma-separated email addresses</div>
                </div>
              }

              <div class="form-check mb-3">
                <input
                  class="form-check-input"
                  type="radio"
                  formControlName="deliveryMethod"
                  value="schedule"
                  id="deliverySchedule"
                />
                <label
                  class="form-check-label"
                  for="deliverySchedule"
                >
                  Schedule Reports
                </label>
              </div>

              @if (generateForm.get('deliveryMethod')?.value === 'schedule') {
                <div class="ms-4 mb-3">
                  <label class="form-label">Frequency</label>
                  <div
                    class="btn-group d-flex"
                    role="group"
                  >
                    <button
                      type="button"
                      class="btn flex-fill"
                      [class.btn-primary]="generateForm.get('scheduleFrequency')?.value === 'weekly'"
                      [class.btn-outline-primary]="generateForm.get('scheduleFrequency')?.value !== 'weekly'"
                      (click)="setScheduleFrequency('weekly')"
                    >
                      Weekly
                    </button>
                    <button
                      type="button"
                      class="btn flex-fill"
                      [class.btn-primary]="generateForm.get('scheduleFrequency')?.value === 'monthly'"
                      [class.btn-outline-primary]="generateForm.get('scheduleFrequency')?.value !== 'monthly'"
                      (click)="setScheduleFrequency('monthly')"
                    >
                      Monthly
                    </button>
                  </div>
                  <div class="form-text mt-2">
                    @if (generateForm.get('scheduleFrequency')?.value === 'weekly') {
                      Reports will be generated every week
                    } @else if (generateForm.get('scheduleFrequency')?.value === 'monthly') {
                      Reports will be generated on the 1st of each month
                    }
                  </div>
                </div>

                <div class="ms-4 mb-3">
                  <label class="form-label">Notification Recipients (ONE Account)</label>
                  <kendo-multiselect
                    formControlName="scheduleRecipients"
                    [data]="filteredRecipients()"
                    textField="name"
                    valueField="email"
                    [valuePrimitive]="false"
                    [autoClose]="false"
                    [filterable]="true"
                    (filterChange)="onRecipientFilterChange($event)"
                    placeholder="Select recipients..."
                    class="w-100"
                  >
                    <ng-template
                      kendoMultiSelectItemTemplate
                      let-dataItem
                    >
                      <div class="d-flex justify-content-between align-items-center w-100">
                        <div>
                          <div class="fw-semibold">{{ dataItem.name }}</div>
                          <div class="text-muted small">{{ dataItem.email }}</div>
                        </div>
                      </div>
                    </ng-template>
                  </kendo-multiselect>
                  <div class="form-text">
                    Select ONE account users who will receive the reports ready email notification
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Date Range (Hidden for Scheduled Reports) -->
          @if (generateForm.get('deliveryMethod')?.value !== 'schedule') {
            <div class="card mb-3">
              <div class="card-body">
                <h5 class="card-title mb-3">Date Range</h5>

                <!-- Quick Date Range Options -->
                <div
                  class="btn-group mb-3 d-flex"
                  role="group"
                >
                  @for (option of dateRangeOptions; track option.label) {
                    <button
                      type="button"
                      class="btn flex-fill"
                      [class.btn-secondary]="selectedDateRange() === option.days"
                      [class.btn-outline-secondary]="selectedDateRange() !== option.days"
                      (click)="applyDateRange(option.days)"
                    >
                      {{ option.label }}
                    </button>
                  }
                  <button
                    type="button"
                    class="btn flex-fill"
                    [class.btn-secondary]="selectedDateRange() === 'custom'"
                    [class.btn-outline-secondary]="selectedDateRange() !== 'custom'"
                    (click)="applyDateRange('custom')"
                  >
                    Custom
                  </button>
                </div>

                <!-- Date Pickers (Only shown when Custom is selected) -->
                @if (selectedDateRange() === 'custom') {
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Start Date</label>
                      <kendo-datepicker
                        formControlName="dateRangeStart"
                        placeholder="Select start date"
                        class="w-100"
                      ></kendo-datepicker>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">End Date</label>
                      <kendo-datepicker
                        formControlName="dateRangeEnd"
                        placeholder="Select end date"
                        class="w-100"
                      ></kendo-datepicker>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      </div>
    </form>
  }
</div>
